import { ReportStatus } from '../api';
import { OldAuditLogEntryProps } from 'src/components/atomsLegacy/OldAuditLogEntry/OldAuditLogEntry';
import { getAuditLogIcon } from 'src/components/moleculesLegacy/CaseAuditLog/helpers';
import theme from 'src/theme/theme';
import { AuditActions, AuditEntry } from 'src/types';
import { DefaultTheme } from 'styled-components';

export const getOldAuditLogEntryProps = (
  auditEntry: AuditEntry,
  lastAuditEntry: boolean,
  t?: DefaultTheme,
): OldAuditLogEntryProps => {
  const isAtlas = t && !t.isVaultVariant;
  const userName = auditEntry.user ? auditEntry.user.name : 'Anonymous';

  const iconSrc = getAuditLogIcon(auditEntry.action, auditEntry.completed);
  let iconAlt = 'Submit Report';
  let color = isAtlas
    ? t.atlas.semantic.color.action.primary.default
    : theme.newPalette.turquoise.T300;
  let children = (
    <>
      {'Report '}
      <span>{'submitted'}</span>
      {`  by ${userName}`}
    </>
  );

  switch (auditEntry.action) {
    case AuditActions.SUBMIT_REPORT:
      color = isAtlas
        ? t.atlas.semantic.color.action.primary.default
        : theme.newPalette.chart.blue;
      break;
    case AuditActions.PUBLIC_SUBMIT_REPORT:
      color = isAtlas
        ? t.atlas.semantic.color.action.primary.default
        : theme.newPalette.chart.blue;
      break;

    case AuditActions.CHANGE_REPORT_ASSIGNEE:
      iconAlt = 'Change Report Assignee';

      const newAssignee =
        auditEntry.data.new &&
        (auditEntry.data.new as unknown as { name: string }).name
          ? (auditEntry.data.new as unknown as { name: string }).name
          : 'another Case Manager';

      children = (
        <>
          {`${userName} `}
          <span>{auditEntry.completed ? 'assigned' : 'failed to assign'}</span>
          {` report to ${newAssignee}`}
        </>
      );
      break;

    case AuditActions.CHANGE_REPORT_STATUS:
      const newStatus = auditEntry.data.new;
      const oldStatus = auditEntry.data.old;

      if (newStatus === ReportStatus.READ) {
        iconAlt = 'Read Report';
        color = isAtlas
          ? t.atlas.semantic.color.action.destructive.default
          : theme.newPalette.red.R300;
        children = (
          <>
            {`${userName} `}
            <span>{auditEntry.completed ? 'opened' : 'failed to open'}</span>
            {' the report'}
          </>
        );
      } else if (newStatus === ReportStatus.INVESTIGATING) {
        iconAlt = 'Investigating Report';
        color = isAtlas
          ? t.atlas.semantic.color.status.warning.default
          : theme.newPalette.yellow.Y300;
        children = (
          <>
            {`${userName} `}
            {auditEntry.completed ? (
              <>
                {'changed report status to '}
                <span>{'in investigation'}</span>
              </>
            ) : (
              <>
                <span>{'failed to change report status'}</span>
                {' to in investigation'}
              </>
            )}
          </>
        );
      } else if (newStatus === ReportStatus.CLOSED) {
        iconAlt = 'Closed Report';
        // Pre-existing bug in vault path: `({ 600: color } = N600)` gives undefined; preserved as-is
        if (isAtlas) {
          color = t.atlas.semantic.color.type.muted;
        } else {
          ({ 600: color } = theme.newPalette.neutral.N600);
        }
        children = (
          <>
            {`${userName} `}
            <span>{auditEntry.completed ? 'closed' : 'failed to close'}</span>
            {' the report'}
          </>
        );
      } else if (
        oldStatus === ReportStatus.DRAFT &&
        newStatus === ReportStatus.NEW
      ) {
        color = isAtlas
          ? t.atlas.semantic.color.action.primary.default
          : theme.newPalette.chart.blue;
      }
      break;

    case AuditActions.CREATE_REPORT_NOTE:
      iconAlt = 'Create Report Note';
      children = (
        <>
          {`${userName} `}
          {auditEntry.completed ? (
            <>
              {'added a '}
              <span>{'note'}</span>
            </>
          ) : (
            <span>{'failed to add a note'}</span>
          )}
        </>
      );
      break;

    case AuditActions.DELETE_REPORT_NOTE:
      iconAlt = 'Delete Report Note';
      children = (
        <>
          {`${userName} `}
          <span>{auditEntry.completed ? 'deleted' : 'failed to delete'}</span>
          {' a note'}
        </>
      );
      break;

    case AuditActions.CREATE_MESSAGE:
      iconAlt = 'Create Message';
      children = (
        <>
          {`${userName} `}
          <span>
            {auditEntry.completed ? 'started' : 'failed to start'}
            {' a conversation'}
          </span>
          {' with Messages'}
        </>
      );
      break;

    case AuditActions.EXPORT_REPORT:
      iconAlt = 'Export Report';
      children = (
        <>
          {`${userName} `}
          <span>{auditEntry.completed ? 'exported' : 'failed to export'}</span>
          {' the report to PDF'}
        </>
      );
      break;

    case AuditActions.CHANGE_DETERMINED_CATEGORY:
      iconAlt = 'Determined Category';
      children = (
        <>
          {`${userName} `}
          <span>
            {auditEntry.completed ? 'changed' : 'failed to change'}
            {' the determined category'}
          </span>
        </>
      );
      break;

    case AuditActions.CREATE_DRAFT:
      iconAlt = 'Create Draft Report';
      color = isAtlas
        ? t.atlas.semantic.color.type.default
        : theme.newPalette.neutral.N800;
      children = (
        <>
          {`${userName} `}
          <span>{'created a draft of'}</span>
          {' the report'}
        </>
      );
      break;

    case AuditActions.UPDATE_REPORT:
      iconAlt = 'Edit Draft Report';
      children = (
        <>
          {`${userName} `}
          <span>{'edited the draft of'}</span>
          {' the report'}
        </>
      );
      break;
    case AuditActions.CHANGE_REPORT_SUBSTANTIATION:
      iconAlt = 'Changed Report Substantiation Status';
      children = auditEntry.data?.new ? (
        <>
          {`${userName}`}
          <span>{' changed the report substantiation status to '}</span>
          {`${auditEntry.data.new}`}
        </>
      ) : (
        <></>
      );
      break;
    case AuditActions.UPDATE_REPORT_SUBMITTED_AT_DATE:
      color = isAtlas
        ? t.atlas.semantic.color.action.primary.default
        : theme.newPalette.turquoise.T300;
      children = auditEntry.data.old ? (
        <>
          {userName} set the report submitted date from the{' '}
          <span>{auditEntry.data.old}</span> to the{' '}
          <span>{auditEntry.data.new}</span>
        </>
      ) : (
        <>
          {userName} set the report submitted date to the{' '}
          <span>{auditEntry.data.new}</span>
        </>
      );
  }
  return {
    auditEntry,
    lastAuditEntry,
    iconSrc,
    iconAlt,
    color,
    children,
  };
};
